import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { rio } from '@amzn/rio-design-tokens';
import { useVerifyOrder } from 'src/hooks/useVerifyOrders';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import useTheme from '@amzn/meridian/theme/use-theme';
import { usePageContext } from 'src/hooks/usePageContext';
import { useAnalyticsContext } from 'src/common/metrics';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { MarketplaceIds } from 'src/utils/marketplace';
import { sendCloseMessage } from 'src/utils/messages';
import { formatDeliveryDate } from 'src/utils/date';
import { YellowButton, PrimeButton } from 'src/common/buttons';
import { ImageBlock, QuantityBadge, RioText, Paragraph, SectionDivider } from 'src/common/styled';
import { DogPage } from 'src/common/errors/DogPage';
import { useBagStore } from 'src/store';
import { Loading } from 'src/common/loaders/Loading';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import { ExternalLink } from 'src/common/externalLink';
import { routes } from 'src/utils/routes';
import { isPaymentError } from 'src/utils/paymentErrors';
import { getOneClickArm, recordOrderPlaced } from 'src/utils/experiments';
export const getVerifyOutcome = ({ orderData, isError, isLoading }) => {
    var _a, _b, _c, _d;
    if (orderData && !orderData.isOrderVerified && isPaymentError((_a = orderData.error) === null || _a === void 0 ? void 0 : _a.errorCode)) {
        return 'paymentError';
    }
    if (isLoading) {
        return 'loading';
    }
    if (orderData && !orderData.isOrderVerified) {
        return 'unverified';
    }
    if (isError || !((_d = (_c = (_b = orderData === null || orderData === void 0 ? void 0 : orderData.orders) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.items) === null || _d === void 0 ? void 0 : _d[0])) {
        return 'genericError';
    }
    return 'success';
};
/**
 * Thank you page — Treatment group (weblab T1, MFA challenge flow).
 * Calls verifyOrder on mount to confirm payment authentication was successful.
 * If verification fails, redirects to the appropriate error page.
 */
export const ThankYouPageVerify = () => {
    var _a, _b, _c, _d, _e;
    const { bundle, appData } = usePageContext();
    const { localizationContext } = useLocalizationContext();
    const redirectUrls = useRetailRedirectUrls([RetailRoutes.ORDER_HISTORY]);
    const rumClient = useAnalyticsContext();
    const theme = useTheme();
    const darkMode = theme.mode === 'dark';
    const location = useLocation();
    const navigate = useNavigate();
    const { listBag } = useBagStore();
    const bagContents = listBag();
    const [searchParams] = useSearchParams();
    // Get purchaseId from state (no MFA redirect) or URL search params (MFA redirect)
    const purchaseId = ((_a = location.state) === null || _a === void 0 ? void 0 : _a.purchaseId) || searchParams.get('purchaseId');
    const { data: orderData, isLoading: isOrderLoading, isError } = useVerifyOrder({ purchaseId });
    const orderItem = (_d = (_c = (_b = orderData === null || orderData === void 0 ? void 0 : orderData.orders) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.items) === null || _d === void 0 ? void 0 : _d[0];
    const outcome = getVerifyOutcome({ orderData, isError, isLoading: isOrderLoading });
    useEffect(() => {
        var _a, _b, _c;
        if (outcome === 'success') {
            // Numerator for C vs T1 orders per session on the 1-Click Checkout dashboard
            recordOrderPlaced(rumClient, {
                purchaseId,
                oneClickArm: getOneClickArm((_a = appData === null || appData === void 0 ? void 0 : appData.data) === null || _a === void 0 ? void 0 : _a.experiments),
                surface: 'THANK_YOU_PAGE',
            });
        }
        else if (outcome === 'paymentError') {
            rumClient.recordEvent('VERIFY_ORDER_PAYMENT_ERROR', {
                purchaseId,
                errorCode: (_b = orderData === null || orderData === void 0 ? void 0 : orderData.error) === null || _b === void 0 ? void 0 : _b.errorCode,
            });
            navigate(`${routes.paymentFailedPage}${location.search}`, { replace: true });
        }
        else if (outcome === 'unverified') {
            rumClient.recordEvent('VERIFY_ORDER_UNVERIFIED', {
                purchaseId,
                errorCode: (_c = orderData === null || orderData === void 0 ? void 0 : orderData.error) === null || _c === void 0 ? void 0 : _c.errorCode,
            });
        }
        else if (outcome === 'genericError' && (isError || orderData)) {
            // A paused query (no data, no error) renders DogPage without a metric, as before
            rumClient.recordEvent('VERIFY_ORDER_GENERIC_ERROR', { purchaseId });
        }
    }, [orderData, isError]);
    const onCloseClick = () => {
        sendCloseMessage(rumClient);
    };
    if (outcome === 'loading' || outcome === 'paymentError') {
        return _jsx(Loading, {});
    }
    if (outcome !== 'success' || !orderItem) {
        return _jsx(DogPage, {});
    }
    // Extract delivery and shipping data from API response
    const deliveryData = formatDeliveryDate('estimatedDelivery', orderItem.estimatedArrival, localizationContext.getLocale());
    const CloseButton = ((_e = bagContents === null || bagContents === void 0 ? void 0 : bagContents[0]) === null || _e === void 0 ? void 0 : _e.isPrime) ? PrimeButton : YellowButton;
    const shippingAddress = orderData === null || orderData === void 0 ? void 0 : orderData.shippingAddress;
    const shipToKey = (shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.isPickupLocation) ? 'shippingToPickup' : 'shippingToCustomer';
    return (_jsxs(Column, { spacing: "400", "data-testid": "order-confirmation-page", children: [_jsxs(Column, { spacingInset: "350 350 none 350", spacing: "none", "data-testid": "success-message-section", children: [_jsxs(Row, { spacing: "300", children: [_jsx(RioText, { rioColor: "success", children: _jsx(Icon, { tokens: checkCircleFilledTokens }) }), _jsx(RioText, { rioColor: "success", type: "h400", "data-font": "display", children: bundle.getMessage('orderPlaced') })] }), _jsx(Text, { children: bundle.getMessage('confirmation') })] }), _jsx(SectionDivider, {}), _jsxs(Column, { spacingInset: "none 350", spacing: "300", "data-testid": "shipping-details-section", children: [_jsx(Text, { type: "b200", children: _jsx("b", { children: bundle.formatMessage(shipToKey, { name: shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.name }) }) }), _jsx("div", { children: shippingAddress ? (_jsxs(Paragraph, { type: "b200", "data-testid": "shipping-address-section", children: [shippingAddress.addressLine1, shippingAddress.addressLine1 && _jsx("br", {}), shippingAddress.city, ", ", shippingAddress.zipCode, shippingAddress.country !== MarketplaceIds.US && _jsx("br", {}), shippingAddress.country !== MarketplaceIds.US && shippingAddress.country] })) : (_jsxs(_Fragment, { children: [_jsx(Row, { backgroundColor: rio.base.color.gray[darkMode ? 600 : 1100], width: "225px", height: "18px" }), _jsx(Row, { backgroundColor: rio.base.color.gray[darkMode ? 600 : 1100], width: "175px", height: "18px" })] })) })] }), _jsx(SectionDivider, {}), _jsxs(Column, { spacingInset: "none 350", spacing: "300", "data-testid": "delivery-details-section", children: [_jsx(Text, { type: "b200", "data-testid": "estimated-delivery", children: _jsx("b", { children: bundle.formatMessage(deliveryData.localeKey, { date: deliveryData.date }) }) }), (bagContents === null || bagContents === void 0 ? void 0 : bagContents.length) > 0 && (_jsx(Row, { children: _jsxs(ImageBlock, { spacing: "none", height: "120px", children: [_jsx("img", { src: bagContents[0].productImageUrl, alt: orderItem.title, "data-testid": "product-image" }), _jsx(QuantityBadge, { alignmentHorizontal: "center", width: "24px", height: "24px", children: _jsx(Text, { type: "b200", "data-testid": "product-quantity", children: orderItem.quantity }) })] }) })), _jsx(Text, { type: "b200", children: _jsx(ExternalLink, { href: redirectUrls.ORDER_HISTORY, id: "order-history-link", source: ReportActionSource.OrderHistory, children: bundle.getMessage('reviewAndEditOrders') }) })] }), _jsx(SectionDivider, {}), _jsx(Column, { spacingInset: "none 350", children: _jsx(CloseButton, { onClick: onCloseClick, "data-testid": "close-button", children: bundle.getMessage('close') }) })] }));
};
